import type { IBoard } from 'store/slices/valueChainMap/interfaces/IBoard';
import type { IValueChainUnit } from 'store/slices/valueChainMap/interfaces/IValueChainUnit';
import type IMoveValueChainBoardUnitParams from './interfaces/IMoveValueChainBoardUnitParams';

const findUnitDeep = (units: IValueChainUnit[], unitId: number): IValueChainUnit | undefined => {
  const directMatch = units.find((unit) => unit.id === unitId);

  if (directMatch) {
    return directMatch;
  }

  return units
    .map((unit) => findUnitDeep(unit.children, unitId))
    .find((found) => found !== undefined);
};

const removeUnitDeep = (units: IValueChainUnit[], unitId: number): IValueChainUnit[] => units
  .filter((unit) => unit.id !== unitId)
  .map((unit) => ({
    ...unit,
    children: removeUnitDeep(unit.children, unitId),
  }));

const placeUnitAtOrderIndex = (
  siblings: IValueChainUnit[],
  unitToPlace: IValueChainUnit,
  orderIndex: number,
): IValueChainUnit[] => [
  ...siblings.map((sibling) => (
    sibling.orderIndex >= orderIndex
      ? { ...sibling, orderIndex: sibling.orderIndex + 1 }
      : sibling
  )),
  unitToPlace,
];

const mapUnitDeep = (
  units: IValueChainUnit[],
  unitId: number,
  transform: (unit: IValueChainUnit) => IValueChainUnit,
): IValueChainUnit[] => units.map((unit) => (
  unit.id === unitId
    ? transform(unit)
    : { ...unit, children: mapUnitDeep(unit.children, unitId, transform) }
));

const removeUnitFromBoard = (board: IBoard, unitId: number): IBoard => ({
  ...board,
  boards: board.boards.map((boardItem) => ({
    ...boardItem,
    columns: boardItem.columns.map((column) => ({
      ...column,
      units: removeUnitDeep(column.units, unitId),
    })),
  })),
});

const insertUnitIntoColumn = (
  board: IBoard,
  columnId: number,
  unitToInsert: IValueChainUnit,
  orderIndex: number,
): IBoard => ({
  ...board,
  boards: board.boards.map((boardItem) => ({
    ...boardItem,
    columns: boardItem.columns.map((column) => (
      column.id === columnId
        ? { ...column, units: placeUnitAtOrderIndex(column.units, unitToInsert, orderIndex) }
        : column
    )),
  })),
});

const insertUnitIntoParent = (
  board: IBoard,
  parentId: number,
  unitToInsert: IValueChainUnit,
  orderIndex: number,
): IBoard => ({
  ...board,
  boards: board.boards.map((boardItem) => ({
    ...boardItem,
    columns: boardItem.columns.map((column) => ({
      ...column,
      units: mapUnitDeep(column.units, parentId, (parentUnit) => ({
        ...parentUnit,
        children: placeUnitAtOrderIndex(parentUnit.children, unitToInsert, orderIndex),
      })),
    })),
  })),
});

const moveValueChainBoardUnit = (
  board: IBoard,
  {
    unitId, columnId, parentId, orderIndex,
  }: IMoveValueChainBoardUnitParams,
): IBoard => {
  const allUnits = board.boards.flatMap((boardItem) => boardItem.columns.flatMap((column) => column.units));
  const unitToMove = findUnitDeep(allUnits, unitId);

  if (!unitToMove) {
    return board;
  }

  const movedUnit: IValueChainUnit = { ...unitToMove, parentId: parentId ?? 0, orderIndex };
  const boardWithoutMovedUnit = removeUnitFromBoard(board, unitId);

  if (parentId) {
    return findUnitDeep(allUnits, parentId)
      ? insertUnitIntoParent(boardWithoutMovedUnit, parentId, movedUnit, orderIndex)
      : board;
  }

  if (columnId === null) {
    return board;
  }

  const targetColumnExists = board.boards
    .some((boardItem) => boardItem.columns.some((column) => column.id === columnId));

  return targetColumnExists
    ? insertUnitIntoColumn(boardWithoutMovedUnit, columnId, movedUnit, orderIndex)
    : board;
};

export default moveValueChainBoardUnit;
